import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import {
  CognitoIdentityProviderClient,
  AssociateSoftwareTokenCommand,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
  VerifySoftwareTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-browser';

// Secure authentication service using AWS Cognito
export class CognitoAuthService {
  private cognitoClient: CognitoIdentityClient;
  private identityPoolId: string;
  private region: string;
  private credentials: any = null;
  private userPoolId: string;
  private userPoolClientId: string;
  private userPoolDomain: string;
  private redirectUri: string;
  private idpClient: CognitoIdentityProviderClient;

  constructor() {
    this.region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    this.identityPoolId = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || '';
    this.userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
    this.userPoolClientId =
      process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID || '';
    this.userPoolDomain = process.env.REACT_APP_COGNITO_USER_POOL_DOMAIN || '';
    this.redirectUri =
      process.env.REACT_APP_COGNITO_REDIRECT_URI || window.location.origin;
    this.idpClient = new CognitoIdentityProviderClient({ region: this.region });

    this.cognitoClient = new CognitoIdentityClient({
      region: this.region,
    });
  }

  private saveAuthResult(result: any) {
    if (!result || !result.IdToken) {
      throw new Error('Authentication failed');
    }
    const idToken = result.IdToken as string;
    const exp = Math.floor(Date.now() / 1000) + (result.ExpiresIn ?? 3600);
    localStorage.setItem('cognito_id_token', idToken);
    localStorage.setItem('cognito_id_token_exp', String(exp));
    this.credentials = null;
  }

  // Native username/password login using USER_PASSWORD_AUTH (no Hosted UI)
  async loginWithPassword(
    username: string,
    password: string
  ): Promise<{
    challenge?:
      | 'NEW_PASSWORD_REQUIRED'
      | 'SMS_MFA'
      | 'SOFTWARE_TOKEN_MFA'
      | 'MFA_SETUP';
    session?: string;
  }> {
    const clientId = this.userPoolClientId;
    if (!clientId) throw new Error('Missing User Pool Client ID');
    const cmd = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: { USERNAME: username, PASSWORD: password },
    } as any);
    const resp = await this.idpClient.send(cmd);
    const challengeName = (resp as any).ChallengeName as string | undefined;
    if (
      challengeName === 'NEW_PASSWORD_REQUIRED' ||
      challengeName === 'SMS_MFA' ||
      challengeName === 'SOFTWARE_TOKEN_MFA' ||
      challengeName === 'MFA_SETUP'
    ) {
      return {
        challenge: challengeName as any,
        session: (resp as any).Session as string,
      };
    }
    const result = (resp as any).AuthenticationResult;
    this.saveAuthResult(result);
    return {};
  }

  async completeNewPassword(
    username: string,
    newPassword: string,
    session: string,
    attrs?: Record<string, string>
  ): Promise<{
    challenge?: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP';
    session?: string;
  }> {
    const clientId = this.userPoolClientId;
    const challengeResponses: any = {
      USERNAME: username,
      NEW_PASSWORD: newPassword,
    };
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        challengeResponses[`userAttributes.${k}`] = v;
      }
    }
    const cmd = new RespondToAuthChallengeCommand({
      ClientId: clientId,
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      Session: session,
      ChallengeResponses: challengeResponses,
    } as any);
    const resp = await this.idpClient.send(cmd);
    const challengeName = (resp as any).ChallengeName as string | undefined;
    if (
      challengeName === 'SMS_MFA' ||
      challengeName === 'SOFTWARE_TOKEN_MFA' ||
      challengeName === 'MFA_SETUP'
    ) {
      return {
        challenge: challengeName as any,
        session: (resp as any).Session as string,
      };
    }
    const result = (resp as any).AuthenticationResult;
    this.saveAuthResult(result);
    return {};
  }

  async respondToMfaChallenge(
    username: string,
    code: string,
    session: string,
    challengeName: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA'
  ) {
    const clientId = this.userPoolClientId;
    const challengeResponses: any = {
      USERNAME: username,
    };
    if (challengeName === 'SMS_MFA') {
      challengeResponses.SMS_MFA_CODE = code;
    } else {
      challengeResponses.SOFTWARE_TOKEN_MFA_CODE = code;
    }
    const cmd = new RespondToAuthChallengeCommand({
      ClientId: clientId,
      ChallengeName: challengeName,
      Session: session,
      ChallengeResponses: challengeResponses,
    } as any);
    const resp = await this.idpClient.send(cmd);
    const result = (resp as any).AuthenticationResult;
    this.saveAuthResult(result);
  }

  async startSoftwareTokenSetup(session: string) {
    const cmd = new AssociateSoftwareTokenCommand({
      Session: session,
    } as any);
    const resp = await this.idpClient.send(cmd);
    return {
      secretCode: (resp as any).SecretCode as string,
      session: (resp as any).Session as string,
    };
  }

  async confirmSoftwareTokenSetup(
    username: string,
    code: string,
    session: string
  ) {
    const verifyCmd = new VerifySoftwareTokenCommand({
      Session: session,
      UserCode: code,
    } as any);
    const verifyResp = await this.idpClient.send(verifyCmd);
    const nextSession = (verifyResp as any).Session || session;

    const respondCmd = new RespondToAuthChallengeCommand({
      ClientId: this.userPoolClientId,
      ChallengeName: 'MFA_SETUP',
      Session: nextSession,
      ChallengeResponses: {
        USERNAME: username,
        ANSWER: 'SOFTWARE_TOKEN_MFA',
      },
    } as any);
    const resp = await this.idpClient.send(respondCmd);
    const result = (resp as any).AuthenticationResult;
    this.saveAuthResult(result);
  }

  async signUp(
    username: string,
    password: string,
    attrs?: Record<string, string>
  ) {
    const cmd = new SignUpCommand({
      ClientId: this.userPoolClientId,
      Username: username,
      Password: password,
      UserAttributes: attrs
        ? Object.entries(attrs).map(([Name, Value]) => ({ Name, Value }))
        : undefined,
    } as any);
    const resp = await this.idpClient.send(cmd);
    return {
      userConfirmed: (resp as any).UserConfirmed as boolean,
    };
  }

  async confirmSignUp(username: string, code: string) {
    const cmd = new ConfirmSignUpCommand({
      ClientId: this.userPoolClientId,
      Username: username,
      ConfirmationCode: code,
    } as any);
    await this.idpClient.send(cmd);
  }

  async resendSignUpCode(username: string) {
    const cmd = new ResendConfirmationCodeCommand({
      ClientId: this.userPoolClientId,
      Username: username,
    } as any);
    await this.idpClient.send(cmd);
  }

  async forgotPassword(username: string) {
    const cmd = new ForgotPasswordCommand({
      ClientId: this.userPoolClientId,
      Username: username,
    });
    await this.idpClient.send(cmd);
  }

  async confirmForgotPassword(
    username: string,
    confirmationCode: string,
    newPassword: string
  ) {
    const cmd = new ConfirmForgotPasswordCommand({
      ClientId: this.userPoolClientId,
      Username: username,
      ConfirmationCode: confirmationCode,
      Password: newPassword,
    });
    await this.idpClient.send(cmd);
  }

  // Get ID token from storage if present and not expired
  private getIdToken(): string | null {
    const raw = localStorage.getItem('cognito_id_token');
    const expRaw = localStorage.getItem('cognito_id_token_exp');
    if (!raw || !expRaw) return null;
    const exp = parseInt(expRaw, 10);
    if (isNaN(exp) || Date.now() / 1000 >= exp - 60) {
      // expired or about to expire
      this.clearCredentials();
      return null;
    }
    return raw;
  }

  // Parse Hosted UI redirect (#id_token=...) and persist
  handleRedirectCallback(): boolean {
    const hash = window.location.hash || '';
    if (!hash.includes('id_token=')) return false;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const idToken = params.get('id_token');
    const expiresIn = parseInt(params.get('expires_in') || '3600', 10);
    if (idToken) {
      // Decode token to determine exp if possible
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1] || '')) || {};
        const exp = payload.exp || Math.floor(Date.now() / 1000) + expiresIn;
        localStorage.setItem('cognito_id_token', idToken);
        localStorage.setItem('cognito_id_token_exp', String(exp));
      } catch {
        const exp = Math.floor(Date.now() / 1000) + expiresIn;
        localStorage.setItem('cognito_id_token', idToken);
        localStorage.setItem('cognito_id_token_exp', String(exp));
      }
      // Clean hash from URL
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search
      );
      return true;
    }
    return false;
  }

  // Begin Hosted UI login (implicit flow)
  login() {
    const base = `https://${this.userPoolDomain}/oauth2/authorize`;
    const redirect = encodeURIComponent(this.redirectUri);
    const scope = encodeURIComponent('openid email profile');
    const url = `${base}?response_type=token&client_id=${this.userPoolClientId}&redirect_uri=${redirect}&scope=${scope}`;
    window.location.assign(url);
  }

  // Hosted UI logout
  logout() {
    // We use native username/password auth; no Hosted UI redirect needed.
    // Simply clear local tokens/credentials; caller handles navigation.
    this.clearCredentials();
  }

  // Get temporary AWS credentials from Cognito Identity Pool (AUTHENTICATED role only)
  async getCredentials() {
    const idToken = this.getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    if (!this.credentials) {
      try {
        const provider = `cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`;
        this.credentials = fromCognitoIdentityPool({
          client: this.cognitoClient,
          identityPoolId: this.identityPoolId,
          logins: { [provider]: idToken },
        });
      } catch (error) {
        console.error('Failed to get Cognito credentials:', error);
        throw new Error('Authentication failed. Please log in again.');
      }
    }
    return this.credentials;
  }

  // Create signed request using temporary credentials
  async createSignedRequest(url: URL, body: any, method: string = 'POST') {
    try {
      const credentials = await this.getCredentials();

      const signer = new SignatureV4({
        credentials,
        region: this.region,
        service: 'lambda',
        sha256: Sha256,
      });

      const signedRequest = await signer.sign({
        protocol: url.protocol.replace(':', '') || 'https',
        method: method,
        hostname: url.hostname || '',
        path: url.pathname || '',
        headers: {
          host: url.hostname || '',
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : null,
      });

      return signedRequest;
    } catch (error) {
      console.error('Failed to create signed request:', error);
      throw new Error('Failed to authenticate request. Please log in again.');
    }
  }

  // Clear credentials (for logout)
  clearCredentials() {
    this.credentials = null;
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_id_token_exp');
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    // allow redirect parsing on load
    this.handleRedirectCallback();
    return !!this.getIdToken();
  }
}

// Singleton instance
export const cognitoAuth = new CognitoAuthService();
